import { useNavigate, useLocation } from "react-router-dom";
import { FOOTER_DISCLAIMER, BRANDS } from "../../data/content";
import { scrollToId } from "../../lib/scroll";

const DIVISION_LINKS = [
    { label: "Healthcare & Medical Tourism", href: "/healthcare-medical-tourism", route: true },
    { label: "International Trading", href: "/international-trading", route: true },
    { label: "Trading & Sourcing from Vietnam", href: "/vietnam-sourcing-agent", route: true },
];

const COMPANY_LINKS = [
    { label: "About", href: "/about", route: true },
    { label: "Insights", href: "/insights", route: true },
    { label: "Contact", href: "/contact", route: true },
];

const LEGAL_LINKS = [
    { label: "Privacy Policy", href: "/legal/privacy-policy" },
    { label: "Terms and Conditions", href: "/legal/terms-and-conditions" },
    { label: "Cookie Policy", href: "/legal/cookie-policy" },
    { label: "Healthcare Disclaimer", href: "/legal/healthcare-disclaimer" },
    { label: "Trading Disclaimer", href: "/legal/trading-disclaimer" },
];

export default function Footer() {
    const navigate = useNavigate();
    const location = useLocation();

    const handle = (e, link) => {
        e.preventDefault();
        if (link.route) {
            navigate(link.href);
            return;
        }
        if (location.pathname !== "/") {
            navigate("/");
            setTimeout(() => scrollToId(link.href), 500);
            return;
        }
        scrollToId(link.href);
    };

    return (
        <footer className="border-t border-white/8 bg-[#04070F]" data-testid="site-footer">
            <div className="mx-auto max-w-[1440px] px-5 pb-10 pt-16 lg:px-10">
                <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-12">
                    <div className="lg:col-span-4">
                        <span className="inline-flex rounded-[3px] bg-ivory px-3 py-2">
                            <img
                                src="/assets/logos/viet-medica-logo.png"
                                alt="Viet Medica Ltd — official logo"
                                loading="lazy"
                                className="h-9 w-auto"
                            />
                        </span>
                        <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.3em] text-gold">
                            Registered in Mauritius
                        </p>
                        <p className="mt-4 max-w-xs text-sm leading-relaxed text-slate-400">
                            An international business group connecting healthcare, medical tourism,
                            travel support and global trading across borders.
                        </p>
                    </div>

                    <nav className="lg:col-span-2" aria-label="Divisions">
                        <h4 className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-500">Divisions</h4>
                        <ul className="mt-5 space-y-3">
                            {DIVISION_LINKS.map((l) => (
                                <li key={l.label}>
                                    <a
                                        href={l.href}
                                        data-testid={`footer-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                        onClick={(e) => handle(e, l)}
                                        className="text-sm text-slate-400 transition-colors hover:text-gold"
                                    >
                                        {l.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <nav className="lg:col-span-3" aria-label="Specialist brands">
                        <h4 className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-500">Specialist Brands</h4>
                        <ul className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                            {BRANDS.map((b) => (
                                <li key={b.id}>
                                    <a
                                        href={`/brands/${b.id}`}
                                        data-testid={`footer-brand-${b.id}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            navigate(`/brands/${b.id}`);
                                        }}
                                        className="text-sm text-slate-400 transition-colors hover:text-gold"
                                    >
                                        {b.name}
                                    </a>
                                </li>
                            ))}
                            <li>
                                <a
                                    href="/about"
                                    data-testid="footer-brand-viet-medica-ltd"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        navigate("/about");
                                    }}
                                    className="text-sm text-slate-400 transition-colors hover:text-gold"
                                >
                                    Viet Medica Ltd
                                </a>
                            </li>
                        </ul>
                    </nav>

                    <nav className="lg:col-span-1" aria-label="Company">
                        <h4 className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-500">Company</h4>
                        <ul className="mt-5 space-y-3">
                            {COMPANY_LINKS.map((l) => (
                                <li key={l.label}>
                                    <a
                                        href={l.href}
                                        data-testid={`footer-${l.label.toLowerCase()}`}
                                        onClick={(e) => handle(e, l)}
                                        className="text-sm text-slate-400 transition-colors hover:text-gold"
                                    >
                                        {l.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <nav className="lg:col-span-2" aria-label="Legal">
                        <h4 className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-500">Legal</h4>
                        <ul className="mt-5 space-y-3">
                            {LEGAL_LINKS.map((l) => (
                                <li key={l.label}>
                                    <a
                                        href={l.href}
                                        data-testid={`footer-legal-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            navigate(l.href);
                                        }}
                                        className="text-sm text-slate-400 transition-colors hover:text-gold"
                                    >
                                        {l.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>
                </div>

                <div className="mt-14 border-t border-white/8 pt-7">
                    <p className="max-w-5xl text-xs leading-relaxed text-slate-500" data-testid="footer-disclaimer">
                        {FOOTER_DISCLAIMER}
                    </p>
                    <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-xs text-slate-600">
                            &copy; 2026 Viet Medica Ltd. All rights reserved.
                        </p>
                        <p className="text-[10px] uppercase tracking-[0.3em] text-slate-600">
                            Mauritius &bull; Vietnam &bull; India &bull; Thailand &bull; China
                        </p>
                    </div>
                </div>
            </div>
        </footer>
    );
}
